import React, { useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export interface MapPlace {
  name: string;
  lat: number;
  lon: number;
  address?: string;
  url?: string;
}

/** The places of a ```map block, or null while it is incomplete or unreadable. */
export function parsePlaces(code: string): MapPlace[] | null {
  try {
    const places = JSON.parse(code)?.places;
    if (!Array.isArray(places)) return null;
    const valid = places.filter(
      (p: any) => p && typeof p.name === 'string' && Number.isFinite(p.lat) && Number.isFinite(p.lon)
    );
    return valid.length ? valid : null;
  } catch {
    return null;
  }
}

const escape = (text: string) =>
  text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

const popup = (p: MapPlace) => {
  const route = `https://www.openstreetmap.org/directions?to=${p.lat}%2C${p.lon}`;
  return [
    `<strong>${escape(p.name)}</strong>`,
    p.address ? `<br><span>${escape(p.address)}</span>` : '',
    '<br>',
    p.url && /^https?:\/\//.test(p.url) ? `<a href="${escape(p.url)}" target="_blank" rel="noopener noreferrer">Site</a> · ` : '',
    `<a href="${route}" target="_blank" rel="noopener noreferrer">Itinéraire</a>`,
  ].join('');
};

const PlacesMap: React.FC<{ places: MapPlace[] }> = ({ places }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    // Wheel zoom only once the map is clicked: scrolling the chat must not zoom it
    const map = L.map(ref.current, { scrollWheelZoom: false });
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    const color = getComputedStyle(ref.current).getPropertyValue('--color-primary').trim() || '#304697';
    const markers = places.map((p) =>
      // Circles, not the default icons: their image files do not survive the bundler
      L.circleMarker([p.lat, p.lon], { radius: 9, color: '#fff', weight: 2, fillColor: color, fillOpacity: 1 })
        .bindPopup(popup(p))
        .addTo(map)
    );
    const bounds = L.latLngBounds(places.map((p) => [p.lat, p.lon] as [number, number]));
    if (places.length === 1) map.setView(bounds.getCenter(), 16);
    else map.fitBounds(bounds, { padding: [32, 32], maxZoom: 17 });
    if (places.length === 1) markers[0].openPopup();

    map.on('click', () => map.scrollWheelZoom.enable());
    map.on('mouseout', () => map.scrollWheelZoom.disable());
    return () => {
      map.remove();
    };
  }, [places]);

  return <div ref={ref} className="places-map" role="region" aria-label="Carte des lieux" />;
};

/** A ```map block of an answer: the map once the block is complete, a placeholder before. */
export const MapBlock: React.FC<{ code: string; isStreaming?: boolean }> = ({ code, isStreaming }) => {
  const places = useMemo(() => parsePlaces(code), [code]);
  if (!places) {
    return isStreaming ? (
      <div className="mermaid-pending" role="status">
        <span className="mermaid-pending-dot" aria-hidden="true" />
        Carte en cours de préparation…
      </div>
    ) : null;
  }
  return <PlacesMap places={places} />;
};

export default PlacesMap;
